import {
  Box, Container, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, Tooltip,
  Switch, FormControlLabel, TextField, InputAdornment, IconButton,
  Skeleton, Paper
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import { useEffect, useRef, useState, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { fetchAllSpecialistWallets } from "../../../redux/admin-slices/wallet.slice"
import TablePagination from "../../../components/common/PaginationSkell"
import PayoutDialog from "../../../components/Dialog/PayoutDialog"
import PauseResumeDialog from "../../../components/Dialog/PauseResumeDialog"
import FlagUnflagDialog from "../../../components/Dialog/FlagUnflagDialog"
import { debounce } from "../../../utils/helper"

const fmt = (n, cur = "inr") => {
  const isUsd = cur?.toLowerCase() === "usd";
  const symbol = isUsd ? "$" : "₹";
  const locale = isUsd ? "en-US" : "en-IN";
  return `${symbol}${(n / 100).toLocaleString(locale, { minimumFractionDigits: 2 })}`;
}

function StripeStatusChip({ onboarded }) {
  return onboarded
    ? <Chip size="small" label="✓ Onboarded" color="success" sx={{ fontWeight: 600 }} />
    : <Chip size="small" label="⚠ Not Set Up" color="warning" sx={{ fontWeight: 600 }} />
}

function RiskChip({ paused, flagged }) {
  if (flagged) return <Chip size="small" label="🚩 Flagged" color="error" sx={{ fontWeight: 600 }} />
  if (paused)  return <Chip size="small" label="⏸ Paused"  color="warning" sx={{ fontWeight: 600 }} />
  return <Chip size="small" label="✓ OK" color="success" />
}

/* ──────────────────────────────────────────────────────────────────────────── */

export default function WalletSpecialists() {
  const dispatch  = useDispatch()
  const navigate  = useNavigate()
  const { loadings } = useSelector(s => s.otherSlice)
  const searchRef = useRef("")

  const [data,             setData]             = useState(null)
  const [onlyWithBalance,  setOnlyWithBalance]  = useState(false)
  const [onlyFlagged,      setOnlyFlagged]      = useState(false)

  // Dialog state
  const [payoutTarget,  setPayoutTarget]  = useState(null) // specialist obj
  const [pauseTarget,   setPauseTarget]   = useState(null) // { specialist, mode }
  const [flagTarget,    setFlagTarget]    = useState(null) // { specialist, mode }

  const load = useCallback((overrides = {}) => {
    dispatch(fetchAllSpecialistWallets({
      search:         searchRef.current?.value || "",
      onlyWithBalance: overrides.onlyWithBalance ?? onlyWithBalance,
      onlyFlagged:     overrides.onlyFlagged     ?? onlyFlagged,
      page:            overrides.currentPage      ?? 1,
      limit:           20,
    }))
      .unwrap()
      .then(setData)
      .catch(() => {})
  // eslint-disable-next-line
  }, [onlyWithBalance, onlyFlagged])

  const handleSearch = debounce(() => load(), 600)

  useEffect(() => { load() }, [load])

  const afterAction = () => load()

  const isLoading = loadings?.["wallet/fetchAllSpecialists"]

  return (
    <Container maxWidth={false} fixed sx={{ py: 5 }}>
      <Box className="overview-container" sx={{ mb: 3 }}>
        <Typography variant="pageTitle">Specialist Wallets</Typography>
        <Typography color="text.secondary" fontSize={14} mt={0.5}>
          View balances and manage payouts for all specialists. Sorted by available balance (highest first).
        </Typography>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3, alignItems: "center" }}>
        <TextField
          inputRef={searchRef}
          placeholder="Search name or email…"
          size="small"
          sx={{ width: 280 }}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={onlyWithBalance}
              onChange={(e) => {
                setOnlyWithBalance(e.target.checked)
                load({ onlyWithBalance: e.target.checked })
              }}
            />
          }
          label="Only with balance"
        />
        <FormControlLabel
          control={
            <Switch
              checked={onlyFlagged}
              onChange={(e) => {
                setOnlyFlagged(e.target.checked)
                load({ onlyFlagged: e.target.checked })
              }}
            />
          }
          label="Only flagged/paused"
        />
      </Box>

      {/* Table */}
      <Box className="content-listing">
        <TableContainer className="adminTable1">
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {["Specialist", "Stripe Status", "Pending", "Available", "Total Paid Out", "Risk Status", "Actions"]
                  .map((h, i) => <TableCell key={i}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading
                ? Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 7 }).map((_, j) => (
                        <TableCell key={j}><Skeleton /></TableCell>
                      ))}
                    </TableRow>
                  ))
                : data?.specialists?.map((s, i) => {
                    const canPayout = s.stripeOnboardingComplete && !s.payoutPaused && !s.flaggedForReview && s.availableBalance > 0
                    return (
                      <TableRow key={i} hover sx={{ td: { borderBottomWidth: i === data.specialists.length - 1 ? 0 : "1px" } }}>
                        <TableCell>
                          <Typography fontWeight={600} textTransform="capitalize" fontSize={14}>{s.fullName}</Typography>
                          <Typography fontSize={12} color="text.secondary">{s.email}</Typography>
                        </TableCell>
                        <TableCell><StripeStatusChip onboarded={s.stripeOnboardingComplete} /></TableCell>
                        <TableCell>
                          <Typography fontWeight={500} fontSize={14}>{fmt(s.pendingBalance, s.currency)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={700} fontSize={14} color={s.availableBalance > 0 ? "success.main" : "text.secondary"}>
                            {fmt(s.availableBalance, s.currency)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={14} color="text.secondary">{fmt(s.totalPaidOut, s.currency)}</Typography>
                        </TableCell>
                        <TableCell><RiskChip paused={s.payoutPaused} flagged={s.flaggedForReview} /></TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                            {/* Pay Out */}
                            <Tooltip title={
                              !s.stripeOnboardingComplete ? "Stripe not onboarded"
                              : s.flaggedForReview ? "Account flagged — unflag first"
                              : s.payoutPaused ? "Payouts paused — resume first"
                              : s.availableBalance === 0 ? "No available balance"
                              : "Trigger payout"
                            } arrow>
                              <span>
                                <Button
                                  size="small" variant="contained" color="primary"
                                  disabled={!canPayout}
                                  onClick={() => setPayoutTarget(s)}
                                  sx={{ textTransform: "none", fontSize: 12 }}
                                >
                                  💳 Pay Out
                                </Button>
                              </span>
                            </Tooltip>

                            {/* Pause / Resume */}
                            {s.payoutPaused
                              ? <Button size="small" variant="outlined" color="success"
                                  onClick={() => setPauseTarget({ specialist: s, mode: "resume" })}
                                  sx={{ textTransform: "none", fontSize: 12 }}>▶ Resume</Button>
                              : <Button size="small" variant="outlined" color="warning"
                                  onClick={() => setPauseTarget({ specialist: s, mode: "pause" })}
                                  sx={{ textTransform: "none", fontSize: 12 }}>⏸ Pause</Button>
                            }

                            {/* Flag / Unflag */}
                            {s.flaggedForReview
                              ? <Button size="small" variant="outlined" color="success"
                                  onClick={() => setFlagTarget({ specialist: s, mode: "unflag" })}
                                  sx={{ textTransform: "none", fontSize: 12 }}>✓ Unflag</Button>
                              : <Button size="small" variant="outlined" color="error"
                                  onClick={() => setFlagTarget({ specialist: s, mode: "flag" })}
                                  sx={{ textTransform: "none", fontSize: 12 }}>🚩 Flag</Button>
                            }

                            {/* View detail */}
                            <Button size="small" variant="text"
                              onClick={() => navigate(`/specialist/detail/${s._id}/wallet`)}
                              sx={{ textTransform: "none", fontSize: 12 }}>👁 View</Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  })
              }
              {!data?.specialists?.length && !isLoading && (
                <TableRow sx={{ td: { borderBottom: "none" } }}>
                  <TableCell colSpan={7}>
                    <Typography variant="noRecords">No specialists found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          pagination={data?.pagination}
          length={data?.specialists?.length}
          loading={isLoading}
          cb={(f) => load({ currentPage: f.currentPage })}
        />
      </Box>

      {/* Dialogs */}
      <PayoutDialog
        open={Boolean(payoutTarget)}
        specialist={payoutTarget}
        onClose={() => setPayoutTarget(null)}
        onSuccess={afterAction}
      />
      <PauseResumeDialog
        open={Boolean(pauseTarget)}
        mode={pauseTarget?.mode}
        specialist={pauseTarget?.specialist}
        onClose={() => setPauseTarget(null)}
        onSuccess={afterAction}
      />
      <FlagUnflagDialog
        open={Boolean(flagTarget)}
        mode={flagTarget?.mode}
        specialist={flagTarget?.specialist}
        onClose={() => setFlagTarget(null)}
        onSuccess={afterAction}
      />
    </Container>
  )
}
